import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DayBreakdown } from '@/api/analyticsService';
import { formatNumber } from '@/lib/format';
import { useChartTheme } from './chartTheme';

interface NewVsReturningChartProps {
  data: DayBreakdown[];
}

interface TooltipPayloadItem {
  payload: DayBreakdown;
}

const NewVsReturningChart = ({ data }: NewVsReturningChartProps) => {
  const t = useChartTheme();

  const renderTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: readonly TooltipPayloadItem[];
    label?: string | number;
  }) => {
    if (!active || !payload?.length) return null;
    const point = payload[0].payload;
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-md dark:border-gray-700 dark:bg-gray-900">
        <p className="mb-1 font-medium text-gray-900 dark:text-white">{label}</p>
        <p className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: t.duo.returning }} />
          Récurrents&nbsp;: <span className="font-semibold">{formatNumber(point.returningUsers)}</span>
        </p>
        <p className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: t.duo.new }} />
          Nouveaux&nbsp;: <span className="font-semibold">{formatNumber(point.newUsers)}</span>
        </p>
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={288}>
      <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
        <CartesianGrid vertical={false} stroke={t.grid} strokeDasharray="3 3" />
        <XAxis
          dataKey="day"
          tick={{ fill: t.axis, fontSize: 11 }}
          tickLine={false}
          axisLine={{ stroke: t.grid }}
          tickFormatter={(v: string) => v.slice(5)}
          minTickGap={24}
        />
        <YAxis
          tick={{ fill: t.axis, fontSize: 12 }}
          tickLine={false}
          axisLine={false}
          width={40}
          allowDecimals={false}
        />
        <Tooltip content={renderTooltip} cursor={{ stroke: t.axis, strokeDasharray: '3 3' }} />
        <Legend
          iconType="circle"
          formatter={(value) => (
            <span className="text-xs text-gray-600 dark:text-gray-300">{value}</span>
          )}
        />
        <Area
          type="monotone"
          name="Récurrents"
          dataKey="returningUsers"
          stackId="1"
          stroke={t.duo.returning}
          strokeWidth={2}
          fill={t.duo.returning}
          fillOpacity={0.85}
        />
        <Area
          type="monotone"
          name="Nouveaux"
          dataKey="newUsers"
          stackId="1"
          stroke={t.duo.new}
          strokeWidth={2}
          fill={t.duo.new}
          fillOpacity={0.85}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default NewVsReturningChart;
